import type { VFXEffectConfig, VFXPrimitive } from '../../VFXEffectRegistry';
import { groundCloud, ringBlast } from './burstBuilders';

/** Essential silhouettes allocate first; static states retain their reduced-motion read. */
function pair(points: number[], color: number, depth: number): VFXPrimitive[] {
  return [-1, 1].map(side => ({ type: 'polygon', polygonPoints: points.map((n, i) => i % 2 ? n : n * side),
    color, alpha: 0.85, depth }));
}
const avatarPlates = pair([22, -18, 36, -21, 44, -9, 39, 10, 29, 16, 24, 5], 0x9b9690, 101);
const sunHalo: VFXPrimitive[] = [-1, 1].map(side => ({
  type: 'arc', arcRadius: 44, arcStartAngle: side < 0 ? 130 : -50, arcEndAngle: side < 0 ? 230 : 50,
  stroke: { color: 0xffd77d, width: 3 }, color: 0xffd77d, alpha: 0.9, depth: 99,
}));

export const ADVANCED_DAMAGE_BUFF_VFX: VFXEffectConfig[] = [
  { id: 'avatar_transform', name: 'Ironwake Rise', category: 'burst', description: 'A brief bronze and steel rise',
    primitives: [groundCloud({ width: 72, height: 18, color: 0x947657, offsetY: 20, durationMs: 400 }),
      ringBlast({ radius: 31, color: 0xdcc898, alpha: 0.65, stroke: { color: 0xf6e8c5, width: 2 }, durationMs: 350, expandTo: 1.25 })] },
  { id: 'avatar_state', name: 'Ironwake Armature', category: 'status', description: 'Steel shoulders edged in war-gold',
    followTarget: 'player', minimumShapes: 2, primitives: [...avatarPlates,
      ...[-1, 1].map((side): VFXPrimitive => ({ type: 'line',
        linePoints: [{ x: 27 * side, y: -14 }, { x: 35 * side, y: -16 }, { x: 39 * side, y: -7 }, { x: 35 * side, y: 7 }],
        lineWidth: 2, color: 0xffd474, alpha: 0.95, depth: 102 }))] },
  { id: 'avenging_wrath_raise', name: 'Sunvow Kindling', category: 'burst', description: 'A segmented solar halo kindles around the oathkeeper',
    primitives: sunHalo.map(arc => ({ ...arc, alpha: 0.65,
      tween: { alpha: 0, scale: 1.15, duration: 400, ease: 'Sine.easeOut' } })) },
  { id: 'avenging_wrath_state', name: 'Sunvow Oath', category: 'status', description: 'A segmented solar halo with paired oath marks',
    followTarget: 'player', minimumShapes: 2, primitives: [...sunHalo,
      ...[-1, 1].map((side): VFXPrimitive => ({ type: 'arc', arcRadius: 38,
        arcStartAngle: side < 0 ? 145 : -35, arcEndAngle: side < 0 ? 215 : 35,
        stroke: { color: 0xfff3cd, width: 1.5 }, color: 0xfff3cd, alpha: 0.8, depth: 99 })),
      ...[-1, 1].map((side): VFXPrimitive => ({ type: 'line',
        linePoints: [{ x: 47 * side, y: -5 }, { x: 52 * side, y: 0 }, { x: 47 * side, y: 5 }],
        lineWidth: 2, color: 0xfff3cd, alpha: 0.95, depth: 99 }))] },
  { id: 'windfury_plant', name: 'Galeheart Release', category: 'burst', description: 'A brief teal gust releases a hovering beacon',
    primitives: [ringBlast({ radius: 18, color: 0x00b3a4, alpha: 0.35, durationMs: 350, expandTo: 1.6 }),
      { type: 'line', linePoints: [{ x: -20, y: 2 }, { x: -5, y: -4 }, { x: 18, y: -1 }],
        lineWidth: 2, color: 0xcffff6, alpha: 0.9, depth: 96, tween: { alpha: 0, y: -10, duration: 350 } }] },
  { id: 'windfury_totem_state', name: 'Galeheart Beacon Field', category: 'status',
    description: 'A hovering wind marker and its circular area, fixed to the accepted cast point', minimumShapes: 2,
    primitives: [
      { type: 'polygon', polygonPoints: [0, -22, 8, -10, 5, 6, 0, 14, -5, 6, -8, -10],
        color: 0x277061, alpha: 0.95, depth: 15 },
      { type: 'arc', arcRadius: 140, arcStartAngle: 0, arcEndAngle: 360,
        color: 0x9ff2e8, alpha: 0.28, depth: 12, stroke: { color: 0x9ff2e8, width: 1.5 } },
      { type: 'polygon', polygonPoints: [0, -15, 3, -7, 0, 6, -3, -7], color: 0x9ff2e8, alpha: 0.9, depth: 16 },
      { type: 'line', linePoints: [{ x: -13, y: -19 }, { x: 2, y: -27 }, { x: 15, y: -21 },
        { x: 4, y: -16 }, { x: -9, y: -10 }, { x: 11, y: -6 }], lineWidth: 2, color: 0xcffff6, alpha: 0.9, depth: 16 },
    ] },
];
